/**
 * Component Loader - Loads feature components on demand
 *
 * Usage:
 *   const modal = await Galigeo.loadComponent('test-modal', document.getElementById('container'));
 *   modal.open({ title: 'Hello' });
 */

// Import shared components (always bundled)
import './shared';

// Registry of feature components with their dynamic import functions
const componentRegistry: Record<string, () => Promise<{ default: CustomElementConstructor }>> = {
  'mapping-wizard-modal': () => import('./features/mapping-wizard-modal'),
  'thematic-wizard-modal': () => import('./features/thematic-wizard/thematic-wizard-modal'),
  'thematic-filter-modal': () => import('./features/thematic-filter-modal'),
  'cache-recalc-modal': () => import('./features/cache-recalc-modal/cache-recalc-modal'),
  'add-data-modal': () => import('./features/data/add-data-modal'),
  'sql-query-builder': () => import('./features/data/sql-query-builder'),
  'sheet-selector-modal': () => import('./features/data/sheet-selector-modal'),
  'measure-picker-modal': () => import('./features/data/measure-picker-modal'),
  'publish-catalog-modal': () => import('./features/data/publish-catalog-modal'),
  'views-modal': () => import('./features/views-modal'),
};

// Map component names to their tag names
const tagNameMap: Record<string, string> = {
  'mapping-wizard-modal': 'ggo-mapping-wizard-modal',
  'thematic-wizard-modal': 'ggo-thematic-wizard-modal',
  'thematic-filter-modal': 'ggo-thematic-filter-modal',
  'cache-recalc-modal': 'ggo-cache-recalc-modal',
  'add-data-modal': 'ggo-add-data-modal',
  'sql-query-builder': 'ggo-sql-query-builder',
  'sheet-selector-modal': 'ggo-sheet-selector-modal',
  'measure-picker-modal': 'ggo-measure-picker-modal',
  'publish-catalog-modal': 'ggo-publish-catalog-modal',
  'views-modal': 'ggo-views-modal',
};

// Cache of loaded components
const loadedComponents = new Set<string>();

export interface LoadComponentOptions {
  /** Properties to set on the component */
  props?: Record<string, unknown>;
  /** Whether to replace the container content (default: true) */
  replace?: boolean;
}

/**
 * Loads a feature component dynamically and renders it in the specified container
 *
 * @param name - Component name (e.g., 'test-modal')
 * @param container - DOM element where the component will be rendered
 * @param options - Optional configuration
 * @returns Promise resolving to the created component element
 *
 * @example
 * // Load and display a modal
 * const modal = await Galigeo.loadComponent('test-modal', document.body);
 * modal.open({ title: 'Confirmation', content: 'Are you sure?' });
 *
 * @example
 * // Load a component with initial props
 * const table = await Galigeo.loadComponent('data-table', container, {
 *   props: { data: myData, columns: myColumns }
 * });
 */
export async function loadComponent<T extends HTMLElement = HTMLElement>(
  name: string,
  container: HTMLElement,
  options: LoadComponentOptions = {}
): Promise<T> {
  const { props = {}, replace = true } = options;

  // Check if component exists in registry
  const importFn = componentRegistry[name];
  if (!importFn) {
    const available = Object.keys(componentRegistry).join(', ');
    throw new Error(`[Galigeo] Unknown component: "${name}". Available: ${available}`);
  }

  // Get the tag name
  const tagName = tagNameMap[name];
  if (!tagName) {
    throw new Error(`[Galigeo] No tag name defined for component: "${name}"`);
  }

  // Dynamically import the component module if not already loaded
  if (!loadedComponents.has(name)) {
    console.log(`[Galigeo] Loading component: ${name}`);
    await importFn();
    // Wait for the custom element to be registered
    await customElements.whenDefined(tagName);
    loadedComponents.add(name);
    console.log(`[Galigeo] Component loaded: ${name}`);
  }

  // Create the component element
  const element = document.createElement(tagName) as T;

  // Set properties
  for (const [key, value] of Object.entries(props)) {
    (element as Record<string, unknown>)[key] = value;
  }

  // Add to container
  if (replace) {
    container.innerHTML = '';
  }
  container.appendChild(element);

  return element;
}

/**
 * Preloads a component without rendering it
 * Useful for preloading components that will be needed soon
 *
 * @param name - Component name to preload
 */
export async function preloadComponent(name: string): Promise<void> {
  const importFn = componentRegistry[name];
  if (!importFn) {
    console.warn(`[Galigeo] Cannot preload unknown component: "${name}"`);
    return;
  }

  const tagName = tagNameMap[name];
  if (!tagName) {
    console.warn(`[Galigeo] No tag name defined for component: "${name}"`);
    return;
  }

  if (!loadedComponents.has(name)) {
    console.log(`[Galigeo] Preloading component: ${name}`);
    await importFn();
    await customElements.whenDefined(tagName);
    loadedComponents.add(name);
    console.log(`[Galigeo] Component preloaded: ${name}`);
  }
}

/**
 * Returns list of available component names
 */
export function getAvailableComponents(): string[] {
  return Object.keys(componentRegistry);
}

/**
 * Checks if a component is already loaded
 */
export function isComponentLoaded(name: string): boolean {
  return loadedComponents.has(name);
}
